import React, { useState } from 'react'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ProposalCategory,
  ProposalStatus
} from '@mprolab/sdk'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import { useGetProposalsQuery } from 'state/services/indexNode/indexNode'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import ProposalRow from './components/ProposalRow'

const StyledTitle = styled(StyledBiennale)`
  font-size: 32px;
`

const FiltersRow = styled(Flex)`
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin: 16px 0 12px;
`

/** Same toolbar as the marketplace: named dropdowns on the left, action right. */
const Toolbar = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 24px 0 20px;

  ${({ theme }) => theme.mediaQueries.tablet} {
    flex-wrap: wrap;
  }
`

const Filters = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const FilterSelect = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 13px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const FilterChip = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray8)};
  background: ${({ theme, $active }) =>
    $active ? `${theme.colors.cyan}22` : 'transparent'};
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const ListShell = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const STATUS_FILTERS: Array<ProposalStatus | undefined> = [
  undefined,
  ProposalStatus.ACTIVE,
  ProposalStatus.PASSED,
  ProposalStatus.REJECTED,
  ProposalStatus.EXECUTED,
  ProposalStatus.CANCELLED
]

const CATEGORY_FILTERS: Array<ProposalCategory | undefined> = [
  undefined,
  ProposalCategory.GOVERNANCE,
  ProposalCategory.TREASURY,
  ProposalCategory.LISTINGS,
  ProposalCategory.PARTNERSHIP,
  ProposalCategory.PARAMETER,
  ProposalCategory.OTHER
]

const DAO: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const { account } = useWeb3React()
  const [status, setStatus] = useState<ProposalStatus | undefined>(undefined)
  const [category, setCategory] = useState<ProposalCategory | undefined>(
    undefined
  )

  const { data, isLoading } = useGetProposalsQuery(
    { status, category, skip: 0, limit: 50 },
    { refetchOnMountOrArgChange: true }
  )

  return (
    <MainWrapper>
      {/* 152px clears the sticky header and the promo bar above it — the
          same offset the Licenses page uses. With mt="32px" the title
          rendered underneath the logo. */}
        <Flex fullWidth flexDirection="column" mt="152px" mb="120px">
        <Flex flexDirection="column" gap="8px">
          <StyledTitle>{t('dao.title')}</StyledTitle>
        </Flex>

        <Toolbar>
          <Filters>
            <FilterSelect
              value={status ?? 'all'}
              onChange={(e) =>
                setStatus(e.target.value === 'all' ? null : (e.target.value as typeof status))
              }
              aria-label={t('dao.filterStatus')}
            >
              <option value="all">{t('dao.allStatuses')}</option>
              {STATUS_FILTERS.filter(Boolean).map((s) => (
                <option key={s as string} value={s as string}>
                  {t(`dao.status.${s}`)}
                </option>
              ))}
            </FilterSelect>

            <FilterSelect
              value={category ?? 'all'}
              onChange={(e) =>
                setCategory(e.target.value === 'all' ? null : (e.target.value as typeof category))
              }
              aria-label={t('dao.filterCategory')}
            >
              <option value="all">{t('dao.allCategories')}</option>
              {CATEGORY_FILTERS.filter(Boolean).map((c) => (
                <option key={c as string} value={c as string}>
                  {t(`dao.category.${c}`)}
                </option>
              ))}
            </FilterSelect>
          </Filters>

          {account ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => navigate('/dao/proposals/new')}
            >
              {t('dao.newProposal')}
            </Button>
          ) : null}
        </Toolbar>

        <ListShell>
          {isLoading ? (
            <Text textAlign="center" color={colors.gray6}>
              {t('dao.loading')}
            </Text>
          ) : (data?.data ?? []).length === 0 ? (
            <Text textAlign="center" color={colors.gray6} mt="40px">
              {t('dao.empty')}
            </Text>
          ) : (
            (data?.data ?? []).map((p) => (
              <ProposalRow
                key={p.proposalId}
                proposal={p}
                onClick={() => navigate(`/dao/proposals/${p.proposalId}`)}
              />
            ))
          )}
        </ListShell>
      </Flex>
    </MainWrapper>
  )
}

export default DAO
